/* lib/payerRequisites.js — проверка ИНН плательщика безнал-счёта (Точка).

   Зеркало серверной `billing._validate_payer_requisites` / `billing.inn_checksum_ok`:
   тип плательщика сверяется с длиной ИНН (ИП — 12 цифр, организация — 10),
   контрольные цифры обязаны сходиться. Окончательное слово за сервером; здесь —
   чтобы человек увидел ошибку у поля, а не получил отказ банка.

   Прод, 14.10.2026: клиент выбрал «ИП» и ввёл ИНН своего ООО. Форма это
   пропускала, банк пять раз отказал, а мы писали «Банк временно недоступен». */

export const onlyDigits = (s) => String(s || '').replace(/\D/g, '');

const W10 = [2, 4, 10, 3, 5, 9, 4, 6, 8];
const W12A = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
const W12B = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];

/** Сходятся ли контрольные цифры ИНН (алгоритм ФНС: сумма с весами mod 11 mod 10). */
export function innChecksumOk(inn) {
  const s = String(inn || '');
  if (!/^\d+$/.test(s) || (s.length !== 10 && s.length !== 12)) return false;
  const d = s.split('').map(Number);
  const check = (w) => (w.reduce((sum, k, i) => sum + k * d[i], 0) % 11) % 10;
  if (d.length === 10) return check(W10) === d[9];
  return check(W12A) === d[10] && check(W12B) === d[11];
}

// Тексты — те же, что отдаёт сервер (billing._validate_payer_requisites).
export const INN_MSG = {
  orgInnForIp: 'ИНН из 10 цифр — это ИНН организации. Выберите тип «ООО» и укажите КПП.',
  ipInnForCompany: 'ИНН из 12 цифр — это ИНН ИП. Выберите тип «ИП».',
  ipLength: 'ИНН ИП — 12 цифр.',
  companyLength: 'ИНН организации — 10 цифр.',
  checksum: 'В ИНН ошибка: не сходится контрольная цифра. Проверьте ИНН по документам.',
};

/** Ошибка ИНН для типа плательщика ('ip' | 'company') или null. Порядок проверок — серверный. */
export function innError(legalType, inn) {
  const d = onlyDigits(inn);
  if (legalType === 'company') {
    if (d.length !== 10) {
      return d.length === 12 && innChecksumOk(d) ? INN_MSG.ipInnForCompany : INN_MSG.companyLength;
    }
  } else if (d.length !== 12) {
    return d.length === 10 && innChecksumOk(d) ? INN_MSG.orgInnForIp : INN_MSG.ipLength;
  }
  return innChecksumOk(d) ? null : INN_MSG.checksum;
}

/** Та же ошибка, но та, что можно показать СРАЗУ, не дожидаясь кнопки.

    Кнопка «Выставить счёт» на невалидной форме заблокирована, так что ошибки,
    показанные только по нажатию, человек не увидел бы вовсе. Недонабранную
    длину не показываем — её называет подсказка под полем, а «12 цифр» на
    каждой набранной цифре только мешает. Перепутанный тип и неверную
    контрольную цифру — показываем.

    `typing` — человек ещё в поле ИНН. Тогда молчим и про «это ИНН организации»:
    первые 10 цифр настоящего ИНН ИП примерно в каждом десятом случае сами
    проходят контрольную организации, и ИП посреди набора своих 12 цифр читал бы
    «выберите ООО». Покажем, когда он уйдёт из поля. */
export function innLiveError(legalType, inn, { typing = false } = {}) {
  const err = innError(legalType, inn);
  if (err === INN_MSG.ipLength || err === INN_MSG.companyLength) return null;
  if (typing && err === INN_MSG.orgInnForIp) return null;
  return err;
}

/** Тип плательщика для подстановки сохранённых реквизитов.

    До 14.10.2026 сервер принимал «ИП» с ИНН организации и сохранял такую пару
    за пространством — подставив её как есть, форма повторила бы отказ банка.
    ИНН с верной контрольной цифрой однозначно говорит тип, ему и верим;
    иначе — сохранённому типу. null — подставлять нечего. */
export function legalTypeForSaved(savedType, inn) {
  const d = onlyDigits(inn);
  if (innChecksumOk(d)) return d.length === 10 ? 'company' : 'ip';
  return savedType === 'ip' || savedType === 'company' ? savedType : null;
}
